/**
 * 1.0.0: YUIChat 项目 - 主应用组件
 * ChatMax 风格布局
 * 1.1.5: 添加认证页面，未登录显示登录页
 * 1.3.14: 添加对话数据页面路由
 * 1.3.40: 添加官网落地页路由
 * 1.3.75: 添加 Onboarding 和组织管理页面
 * 1.3.80: Landing Page 作为域名主入口，应用主页改为 /app
 * 1.3.81: 添加注册页面路由，登录和注册分离
 * 1.3.96: 添加特定商業取引法ページ路由
 */

import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './components/Sidebar';
import { TopNav } from './components/TopNav';
import { ChatInterface } from './components/ChatInterface';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { AllProjectsPage } from './pages/AllProjectsPage';
import { SharePage } from './pages/SharePage';
import { PublicChatPage } from './pages/PublicChatPage'; // 1.2.24: 公开聊天页面
import { SettingsPage } from './pages/SettingsPage';
import { DashboardPage } from './pages/DashboardPage';
import { ConversationDataPage } from './pages/ConversationDataPage'; // 1.3.14: 对话数据页面
import { QAPage } from './pages/QAPage'; // 1.3.30: QA问答管理页面
import { AuthPage } from './pages/AuthPage';
import { RegisterPage } from './pages/RegisterPage'; // 1.3.81: 注册选择页面
import { BusinessRegisterPage } from './pages/BusinessRegisterPage'; // 1.3.81: 企业用户注册页面
import { AccountCenterPage } from './pages/AccountCenterPage'; // 1.2.29: 账号中心
import { FeedbackPage } from './pages/FeedbackPage'; // 1.2.29: 意见反馈
import { UpgradePlanPage } from './pages/UpgradePlanPage'; // 1.3.74: 套餐升级页面
import { LandingPage } from './pages/LandingPage'; // 1.3.40: 官网落地页
import { OnboardingPage } from './pages/OnboardingPage'; // 1.3.75: Onboarding 页面
import { BusinessSetupPage } from './pages/BusinessSetupPage'; // 1.3.75: 企业信息页面
import { OrganizationSettingsPage } from './pages/OrganizationSettingsPage'; // 1.3.75: 组织设置
import { MembersPage } from './pages/MembersPage'; // 1.3.75: 成员管理
import { CommercialDisclosurePage } from './pages/CommercialDisclosurePage'; // 1.3.96: 特定商業取引法
import { TermsOfServicePage } from './pages/TermsOfServicePage'; // 1.4.3: 利用規約
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'; // 1.4.3: プライバシーポリシー
import { InviteAcceptPage } from './pages/InviteAcceptPage'; // 1.4.5: 邀请接受页面
import { AuthModal } from './components/AuthModal';
import { getCurrentUser, onAuthStateChange } from './services/authService';
import { getOnboardingStatus, completeBusinessOnboarding, completePersonalOnboarding, acceptInvitation } from './services/organizationService'; // 1.3.75, 1.3.81, 1.4.2
import { isSupabaseAvailable } from './lib/supabase';
import type { User } from '@supabase/supabase-js';
import './i18n';

function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // 1.1.5: 添加加载状态
  const [needsOnboarding, setNeedsOnboarding] = useState(false); // 1.3.75: Onboarding 状态

  // Check if current route should show sidebar and top nav
  // 1.2.6: 外部分享管理页面显示侧边栏，只有公开分享链接（带参数）不显示
  // 1.3.23: /api/share/:token 为 OG 入口，按公开分享处理
  // 1.3.75: Onboarding 和 Business Setup 页面也不显示
  // 1.3.80: Landing Page (/) 不显示侧边栏
  // 1.3.81: 注册相关页面不显示侧边栏
  // 1.3.96: 法律页面不显示侧边栏
  // 1.4.3: 添加 /terms 和 /privacy 页面
  const showLayout = !(
    ((location.pathname.startsWith('/share/') && location.pathname !== '/share') ||
     location.pathname.startsWith('/api/share/')) ||
    location.pathname === '/' ||
    location.pathname === '/onboarding' ||
    location.pathname === '/business-setup' ||
    location.pathname === '/register' ||
    location.pathname === '/business-register' ||
    location.pathname === '/commercial-disclosure' ||
    location.pathname === '/terms' ||
    location.pathname === '/privacy' ||
    location.pathname.startsWith('/invite/')
  );

  useEffect(() => {
    // Load initial user
    const loadUser = async () => {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      // 1.3.78: 检查用户是否需要完成 Onboarding
      // 1.3.81: 检查是否有待处理的注册信息
      if (currentUser) {
        const { status } = await getOnboardingStatus();
        
        // 1.3.81: 检查是否有待处理的企业注册信息
        const pendingBusinessSetup = localStorage.getItem('pendingBusinessSetup');
        if (pendingBusinessSetup && status?.needs_onboarding) {
          try {
            const businessInfo = JSON.parse(pendingBusinessSetup);
            // 自动完成企业注册
            const { error } = await completeBusinessOnboarding(
              businessInfo.companyName,
              businessInfo.companySize,
              businessInfo.industry,
              businessInfo.jobTitle
            );
            // 1.5.12: 只在 API 成功时清除待处理信息
            // 修复：之前无论成功失败都清除，导致 API 失败时用户数据丢失无法重试
            if (!error) {
              localStorage.removeItem('pendingBusinessSetup');
              setNeedsOnboarding(false);
              setIsLoading(false);
              return;
            }
            // 1.5.16: API 失败时不设置 needsOnboarding 也不提前返回
            // 让代码继续执行到下方的正常 onboarding 状态检查
            // 这样避免了无限重定向循环（设置 needsOnboarding=true + pendingBusinessSetup 保留会导致）
            // pendingBusinessSetup 数据保留，下次 auth 事件（如页面刷新、token 刷新）时自动重试
          } catch (e) {
            // 解析失败，清除无效数据并继续正常 onboarding 流程
            localStorage.removeItem('pendingBusinessSetup');
          }
        }
        
        // 1.4.2: 检查是否有待处理的邀请链接
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          localStorage.removeItem('pendingInviteToken');
          // 跳转到邀请接受页面
          navigate(`/invite/${pendingInviteToken}`);
          setIsLoading(false);
          return;
        }
        
        // 1.3.88: 移除自动创建组织逻辑，避免与用户选择冲突
        // SSO 用户需要通过 Onboarding 页面选择类型
        if (status?.needs_onboarding) {
          setNeedsOnboarding(true);
        } else {
          setNeedsOnboarding(false);
        }
      }
      
      setIsLoading(false);
    };

    loadUser();

    // Subscribe to auth changes
    // 1.6.21: 使用 ref 保存当前用户 ID，用于检测用户是否真正改变
    let previousUserId: string | null = null;
    
    const { unsubscribe } = onAuthStateChange(async (currentUser, event) => {
      // 1.6.21: 忽略 TOKEN_REFRESHED 事件，避免切换标签页时页面刷新
      // TOKEN_REFRESHED 只是刷新了 token，用户状态没有改变，不需要重新加载页面
      if (event === 'TOKEN_REFRESHED') {
        // Token 刷新时只更新用户信息，不触发加载状态
        setUser(currentUser);
        return;
      }

      // 1.6.60: PASSWORD_RECOVERY 事件 — Supabase JS 检测到 recovery hash 并建立了 session。
      // 不要触发 isLoading=true（会卸载 AuthPage 导致丢失 recovery 状态），
      // 只更新 user 并确保 AuthPage 能显示重置密码表单。
      if (event === 'PASSWORD_RECOVERY') {
        previousUserId = currentUser?.id ?? null;
        setUser(currentUser);
        setIsLoading(false);
        return;
      }
      
      // 1.6.21: 检查用户是否真正改变（从登录变未登录，或从未登录变登录，或换了用户）
      const currentUserId = currentUser?.id ?? null;
      const userChanged = previousUserId !== currentUserId;
      previousUserId = currentUserId;
      
      // 如果用户没有改变，不触发加载状态
      if (!userChanged && currentUser) {
        setUser(currentUser);
        return;
      }
      
      // 1.5.11: 设置 loading 状态，与 loadUser 保持一致
      // 确保在获取 onboarding 状态时显示加载动画
      setIsLoading(true);
      setUser(currentUser);
      
      // 1.3.78: 检查用户是否需要完成 Onboarding
      // 1.3.81: 检查是否有待处理的注册信息（企业用户通过邮箱验证后自动完成注册）
      if (currentUser) {
        const { status } = await getOnboardingStatus();
        
        // 1.3.81: 检查是否有待处理的企业注册信息
        const pendingBusinessSetup = localStorage.getItem('pendingBusinessSetup');
        if (pendingBusinessSetup && status?.needs_onboarding) {
          try {
            const businessInfo = JSON.parse(pendingBusinessSetup);
            // 自动完成企业注册
            const { error } = await completeBusinessOnboarding(
              businessInfo.companyName,
              businessInfo.companySize,
              businessInfo.industry,
              businessInfo.jobTitle
            );
            // 1.5.12: 只在 API 成功时清除待处理信息
            // 修复：之前无论成功失败都清除，导致 API 失败时用户数据丢失无法重试
            if (!error) {
              localStorage.removeItem('pendingBusinessSetup');
              setNeedsOnboarding(false);
              setIsLoading(false);
              return;
            }
            // 1.5.16: API 失败时不设置 needsOnboarding 也不提前返回
            // 让代码继续执行到下方的正常 onboarding 状态检查
            // 这样避免了无限重定向循环（设置 needsOnboarding=true + pendingBusinessSetup 保留会导致）
            // pendingBusinessSetup 数据保留，下次 auth 事件（如页面刷新、token 刷新）时自动重试
          } catch (e) {
            // 解析失败，清除无效数据并继续正常 onboarding 流程
            localStorage.removeItem('pendingBusinessSetup');
          }
        }
        
        // 1.4.2: 检查是否有待处理的邀请链接
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          localStorage.removeItem('pendingInviteToken');
          // 跳转到邀请接受页面
          navigate(`/invite/${pendingInviteToken}`);
          setIsLoading(false);
          return;
        }
        
        // 1.3.88: 移除自动创建组织逻辑，避免与用户选择冲突
        if (status?.needs_onboarding) {
          setNeedsOnboarding(true);
        } else {
          setNeedsOnboarding(false);
        }
      } else {
        setNeedsOnboarding(false);
      }
      
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleAuthSuccess = () => {
    setIsAuthModalOpen(false);
    getCurrentUser().then(setUser);
  };
  
  // 1.3.75: Onboarding 完成回调
  // 1.3.80: 跳转到应用主页 /app
  const handleOnboardingComplete = () => {
    setNeedsOnboarding(false);
    navigate('/app');
  };

  // 1.3.80: 路径判断变量（提前定义以供 useEffect 使用）
  // 1.3.81: 添加注册相关页面判断
  // 1.3.96: 添加特定商業取引法页面判断
  const isPublicShareLink = (location.pathname.startsWith('/share/') && location.pathname !== '/share') ||
                            location.pathname.startsWith('/api/share/');
  const isLandingPage = location.pathname === '/';
  const isOnboardingPage = location.pathname === '/onboarding' || location.pathname === '/business-setup';
  const isRegisterPage = location.pathname === '/register' || location.pathname === '/business-register';
  // 1.4.3: 添加 /terms 和 /privacy 页面
  const isLegalPage = location.pathname === '/commercial-disclosure' || 
                      location.pathname === '/terms' || 
                      location.pathname === '/privacy';
  
  // 1.3.80: 已登录用户访问 landing page 时，重定向到应用主页
  // 1.4.3: 如果有滚动标记（从法律页面跳转），则不重定向，让 LandingPage 处理滚动
  useEffect(() => {
    if (user && isLandingPage && !needsOnboarding && !isLoading) {
      const hasScrollIntent = sessionStorage.getItem('scrollToFooter') === 'true' || 
                              sessionStorage.getItem('scrollToPricing') === 'true';
      if (!hasScrollIntent) {
        navigate('/app');
      }
    }
  }, [user, isLandingPage, needsOnboarding, isLoading, navigate]);

  // 1.1.5: 加载中显示加载状态
  // 1.2.58: 添加动态点点点效果
  // 1.3.27: 页面loading只显示YUI动画，移除"AI正在思考中"文字
  if (isLoading && isSupabaseAvailable) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <span className="inline-block text-primary font-semibold yui-loading-animation text-2xl">YUI</span>
      </div>
    );
  }

  // 1.1.5: 如果用户未登录且不是分享页面，显示认证页面
  // 1.2.6: 只有公开分享链接（带参数）不需要登录，管理页面需要登录
  // 1.3.40: 落地页不需要登录
  // 1.3.78: 恢复 Onboarding 检查，新用户登录后跳转选择页面
  // 1.3.80: Landing Page 改为根路径 /
  // 1.3.81: 注册页面不需要登录
  // 1.3.96: 法律页面（特定商業取引法等）不需要登录
  // 1.4.2: 邀请链接页面不需要登录（在页面内处理登录流程）
  const isInvitePage = location.pathname.startsWith('/invite/');
  if (!user && isSupabaseAvailable && !isPublicShareLink && !isLandingPage && !isRegisterPage && !isLegalPage && !isInvitePage && !location.pathname.startsWith('/auth')) {
    return <AuthPage />;
  }
  
  // 1.3.78: 新用户需要完成 Onboarding
  // 1.3.90: 如果当前在 /auth 页面（邮件确认回调），让 AuthPage 处理，不跳转 Onboarding
  // 1.5.1: 如果在邀请页面，让 InviteAcceptPage 处理，不跳转 Onboarding
  const isAuthPage = location.pathname === '/auth' || location.pathname.startsWith('/auth');
  if (user && needsOnboarding && !isOnboardingPage && !isPublicShareLink && !isAuthPage && !isInvitePage) {
    return <OnboardingPage onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Routes>
        {/* Landing Page - No layout, no login required */}
        {/* 1.3.40: 官网落地页，支持多语言 */}
        {/* 1.3.80: Landing Page 作为域名主入口 */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Auth Page - No layout */}
        <Route path="/auth" element={<AuthPage />} />
        
        {/* 1.3.81: Register Pages - No layout, no login required */}
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/business-register" element={<BusinessRegisterPage />} />
        
        {/* 1.3.96: Legal Pages - No layout, no login required */}
        {/* 1.4.3: 添加利用規約和プライバシーポリシー路由 */}
        <Route path="/commercial-disclosure" element={<CommercialDisclosurePage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        
        {/* 1.4.5: 邀请接受页面 - No layout, no login required */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        
        {/* 1.3.75: Onboarding Pages - No layout */}
        <Route path="/onboarding" element={<OnboardingPage onComplete={handleOnboardingComplete} />} />
        <Route path="/business-setup" element={<BusinessSetupPage onComplete={handleOnboardingComplete} />} />
        
        {/* Public Chat Page - No layout, no login required */}
        {/* 1.2.24: 公开聊天页面，通过 share_token 访问，无需登录 */}
        <Route path="/share/:shareToken" element={<PublicChatPage />} />
        {/* 1.3.23: 兼容 /api/share/:token（本地开发无 Edge Function） */}
        <Route path="/api/share/:shareToken" element={<PublicChatPage />} />

        {/* Main App Routes */}
        <Route
          path="*"
          element={
            <div className="flex h-screen overflow-hidden">
              {/* Sidebar */}
              {showLayout && (
                <Sidebar
                  isCollapsed={isSidebarCollapsed}
                  onCollapsedChange={setIsSidebarCollapsed}
                />
              )}

              {/* Main Content */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Top Nav - Only show on knowledge base pages */}
                {showLayout && location.pathname.startsWith('/knowledge-base') && (
                  <TopNav />
                )}

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto">
                  <Routes>
                    {/* 1.3.80: 应用主页改为 /app */}
                    <Route path="/app" element={<AllProjectsPage />} />
                    <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                    <Route path="/knowledge-base/documents" element={<KnowledgeBasePage />} />
                    <Route path="/knowledge-base/qa" element={<QAPage />} />
                    <Route path="/chat" element={<ChatInterface />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
                    <Route path="/settings/members" element={<MembersPage />} />
                    <Route path="/share" element={<SharePage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/conversation-data" element={<ConversationDataPage />} />
                    <Route path="/account" element={<AccountCenterPage />} />
                    <Route path="/upgrade" element={<UpgradePlanPage />} />
                    <Route path="/feedback" element={<FeedbackPage />} />
                  </Routes>
                </main>
              </div>

              {/* Auth Modal */}
              <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onSuccess={handleAuthSuccess}
              />
            </div>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
